/**
 * LoginPage — Google Sign-In using Identity Services.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../stores/authStore';
import { apiUrl } from '../../config';
import './LoginPage.css';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '1005843682839-dcceb3tt91lc3q5ltjofm2sio70dgh1q.apps.googleusercontent.com';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: any) => void;
          renderButton: (element: HTMLElement, config: any) => void;
          prompt: () => void;
        };
      };
    };
  }
}

export default function LoginPage() {
  const { t } = useTranslation('entrance');
  const buttonRef = useRef<HTMLDivElement>(null);
  const setAuth = useAuthStore((s) => s.setAuth);
  const existingUser = useAuthStore((s) => s.user);
  const existingToken = useAuthStore((s) => s.token);
  const navigate = useNavigate();
  const [loginError, setLoginError] = useState<string | null>(null);

  // If already authenticated, redirect away from login page
  useEffect(() => {
    if (existingUser && existingToken) {
      const redirectTo = localStorage.getItem('dukkan_redirect_after_login');
      localStorage.removeItem('dukkan_redirect_after_login');
      navigate(redirectTo || '/shops', { replace: true });
    }
  }, [existingUser, existingToken, navigate]);

  useEffect(() => {
    // Load the Google Identity Services script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.onload = () => {
      window.google?.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleCredentialResponse,
      });

      if (buttonRef.current) {
        window.google?.accounts.id.renderButton(buttonRef.current, {
          theme: 'outline',
          size: 'large',
          width: 300,
          text: 'signin_with',
          shape: 'rectangular',
        });
      }
    };
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  async function handleCredentialResponse(response: { credential: string }) {
    try {
      console.log('🔐 [Login] Google credential received, sending to server...');
      const res = await fetch(apiUrl('/api/auth/google'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential: response.credential }),
      });

      if (!res.ok) {
        console.error('🔐 [Login] Auth failed:', res.status);
        setLoginError(t('loginFailed') || 'Login failed. Please try again.');
        return;
      }

      const data = await res.json();
      console.log('🔐 [Login] Auth success, setting auth state...');
      // Server now returns { sessionToken, user } — store the session token
      const sessionToken: string = data.sessionToken;
      const user = data.user;
      // Check for pending redirect BEFORE setAuth triggers the useEffect
      const pendingRedirect = localStorage.getItem('dukkan_redirect_after_login');
      if (pendingRedirect) localStorage.removeItem('dukkan_redirect_after_login');
      // Store auth state — this triggers re-render and the useEffect above
      // will call navigate(). Using React Router navigation instead of
      // window.location.href avoids Safari issues where localStorage writes
      // can be lost during a full page reload.
      setAuth(user, sessionToken);

      // Merge guest activity into the authenticated user (fire-and-forget)
      const guestId = localStorage.getItem('dukkan_guest_id');
      if (guestId && user.id) {
        import('../../services/activityService').then(({ mergeGuestActivity }) => {
          mergeGuestActivity(guestId, user.id);
        });
      }

      // Fallback: if the useEffect doesn't run (stale closure), navigate directly
      // setTimeout ensures localStorage is flushed before any potential reload  
      setTimeout(() => {
        navigate(pendingRedirect || '/shops', { replace: true });
      }, 100);
    } catch (e) {
      console.error('🔐 [Login] Login error:', e);
      setLoginError(t('loginError') || 'Something went wrong. Please try again.');
    }
  }

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-logo">🏪</div>
        <h1>{t('loginTitle')}</h1>
        <p className="login-subtitle">{t('loginSubtitle')}</p>
        <div className="login-divider" />
        <div ref={buttonRef} className="google-btn-container" />
        {loginError && (
          <p className="login-error">{loginError}</p>
        )}
        <p className="login-footer">{t('loginFooter')}</p>
        <Link to="/" className="login-home-link">
          ← {t('backToHome') || 'Back to Home'}
        </Link>
      </div>
    </div>
  );
}
